import Utils from './utils';

/**
 * @class TableUtils
 * @description General functions used in our implementation of the Material UI Table component
 */
export default class TableUtils {
    static stableSort(array, cmp) {
        const stabilizedThis = array.map((el, index) => [el, index]);

        stabilizedThis.sort((a, b) => {
            const order = cmp(a[0], b[0]);

            if (order !== 0) {
                return order;
            }

            return a[1] - b[1];
        });

        return stabilizedThis.map(el => el[0]);
    }

    /**
     * Calls the sorting function and returns it normal or inverted, depending on the given sorting order
     * @param {string} order - sorting order (desc / asc)
     * @param {string} orderBy - the key to sort by
     * @param {Function} sort - the actual sorting function
     * @param {Object} [normaliseList = null] normaliseList - lookup table to convert value to something else (for example, a status into a number)
     * @returns {*} sorted entries
     */
    static getSorting(order, orderBy, sort, normaliseList = null) {
        return order === 'desc' ? (a, b) => sort(a, b, orderBy, normaliseList) : (a, b) => -sort(a, b, orderBy, normaliseList);
    }

    /**
     * Sorts the given entries by given order, with its value optionally converted to a number via a lookup table (normaliseList)
     * @param {(string|Object)} a - first value
     * @param {(string|Object)} b - second value
     * @param {string} orderBy - the key to perform the comparison on
     * @param {Object} [normaliseList = null] normaliseList - lookup table to convert value to something else (for example, a status into a number)
     * @returns {*} sorted entries
     */
    static desc(a, b, orderBy, normaliseList = null) {

        if (Utils.isArray(a[orderBy]) && Utils.isArray(b[orderBy])) {

            // when encountering an array instead of a string value, sort on the first entry of it
            const aValue = a[orderBy][0].value;
            const bValue = b[orderBy][0].value;

            if (bValue < aValue) {
                return -1;
            }

            if (bValue > aValue) {
                return 1;
            }
        } else if (a[orderBy] === null || b[orderBy] === null) {

            if (a[orderBy] === null) {
                return 1;
            }

            if (a[orderBy] !== null && b[orderBy] === null) {
                return -1;
            }

        } else if (typeof (a[orderBy]) === 'object' && typeof (b[orderBy]) === 'object') {

            // when encountering an object instead of a string value, consider it a translated value and extract the translatedValue key
            let aValue = a[orderBy].value;
            let bValue = b[orderBy].value;

            // check if normaliseList was supplied to convert value to number
            if (normaliseList) {
                aValue = normaliseList[Utils.camelCaseToSnakeCase(aValue).toUpperCase()] || 0;
                bValue = normaliseList[Utils.camelCaseToSnakeCase(bValue).toUpperCase()] || 0;
            }

            if (bValue < aValue) {
                return -1;
            }

            if (bValue > aValue) {
                return 1;
            }
        } else {

            // dealing with normal string value
            if (b[orderBy] < a[orderBy]) {
                return -1;
            }

            if (b[orderBy] > a[orderBy]) {
                return 1;
            }
        }

        return 0;
    }

    /**
     * @function localizeTableData
     * @param {array} data - array containing objects containing translatable keys
     * @param {array} fields - array containing the fields that should be translated
     * @param {Object} i18n - i18n translation object
     * @param {string} i18nInfix - i18n translation infix
     * @returns { Array } translated values
     */
    static localizeTableData(data, fields, i18n, i18nInfix) {
        const array = [];

        data.forEach(row => {
            Object.keys(row).forEach(key => {

                // replace received static string value for status field with object containing original and translated value
                if (fields.includes(key) && typeof row[key] === 'string') {
                    row[key] = {
                        value: row[key],
                        translatedValue: i18n[`${i18nInfix}_${Utils.camelCaseToSnakeCase(key)}`] || row[key]
                    };
                }
            });

            array.push(row);
        });

        return array;
    }
}
